import React, { useState } from "react";
import { makeStyles } from "@material-ui/core";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Pie, Doughnut } from "react-chartjs-2";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PALETTE = ["#003087", "#C8102E", "#00843D", "#F4A300", "#6B48C8", "#A0A0A0"];

function hexToRgb(hex) {
  var r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [0, 0, 200];
}

function interpolateColor(hex, intensity) {
  var rgb = hexToRgb(hex);
  return "rgb(" +
    Math.round(255 + (rgb[0] - 255) * intensity) + "," +
    Math.round(255 + (rgb[1] - 255) * intensity) + "," +
    Math.round(255 + (rgb[2] - 255) * intensity) + ")";
}

function formatHeatmapValue(val) {
  if (!val || val === 0) return "\u2014";
  var abs = Math.abs(val);
  var sign = val < 0 ? "-" : "";
  if (abs >= 1e9) return sign + (abs / 1e9).toFixed(1) + "B";
  if (abs >= 1e6) return sign + (abs / 1e6).toFixed(1) + "M";
  if (abs >= 1e3) return sign + (abs / 1e3).toFixed(1) + "K";
  return sign + abs.toLocaleString("en-US");
}

function buildWaterfallData(chart) {
  var ds0 = chart.datasets && chart.datasets[0];
  var values = (ds0 && ds0.data) ? ds0.data : [];
  var barColors = (chart.bar_colors && chart.bar_colors.length) ? chart.bar_colors : null;
  var cumulatives = [0];
  for (var ci = 0; ci < values.length; ci++) {
    cumulatives.push(cumulatives[ci] + values[ci]);
  }
  var floatingData = values.map(function (val, i) {
    if (i === 0 || i === values.length - 1) {
      return [Math.min(0, val), Math.max(0, val)];
    }
    return [
      Math.min(cumulatives[i], cumulatives[i + 1]),
      Math.max(cumulatives[i], cumulatives[i + 1]),
    ];
  });
  var bgColors = barColors || values.map(function (v) { return v >= 0 ? "#003087" : "#C8102E"; });
  return {
    labels: chart.labels,
    datasets: [{
      label: (ds0 && ds0.label) || "",
      data: floatingData,
      backgroundColor: bgColors,
      borderColor: "transparent",
      borderWidth: 0,
      borderSkipped: false,
    }],
  };
}

function getChartHeight(labels, chartType) {
  if (chartType === "pie" || chartType === "doughnut") return 350;
  if (chartType === "heatmap") return null;
  if (!labels) return 320;
  if (labels.length <= 6) return 320;
  if (labels.length <= 15) return 420;
  return 520;
}

const useStyles = makeStyles(() => ({
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  wrapper: {
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    padding: "20px",
    marginTop: "12px",
    position: "relative",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    animation: "$fadeInUp 250ms ease-out 280ms both",
    "&:hover $copyBtn, &:hover $expandBtn": {
      opacity: 1,
    },
  },
  title: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "12px",
    fontFamily: "'DM Sans', sans-serif",
  },
  chartContainer: {
    position: "relative",
    transition: "height 300ms ease-out",
  },
  copyBtn: {
    position: "absolute",
    top: "8px",
    right: "8px",
    opacity: 0,
    transition: "opacity 150ms",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    padding: "4px 6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    "&:hover": {
      background: "#f3f4f6",
    },
  },
  expandBtn: {
    position: "absolute",
    top: "8px",
    right: "44px",
    opacity: 0,
    transition: "opacity 150ms",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    padding: "4px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    zIndex: 2,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "11px",
    fontWeight: 500,
    color: "#6B7280",
    "&:hover": {
      background: "#f3f4f6",
      color: "#374151",
    },
  },
  copyBtnCopied: {
    background: "#f0fdf4",
    borderColor: "#86efac",
    color: "#16a34a",
    fontSize: "11px",
    fontWeight: 600,
    padding: "4px 8px",
    opacity: 1,
  },
}));

var ChartRenderer = React.memo(function ChartRenderer({ chart }) {
  var classes = useStyles();
  var _useCopied = useState(false);
  var copied = _useCopied[0];
  var setCopied = _useCopied[1];
  var _useExpanded = useState(false);
  var isExpanded = _useExpanded[0];
  var setIsExpanded = _useExpanded[1];

  if (!chart || !chart.chart_type || !chart.labels) return null;

  var chartType = chart.chart_type;
  var isPieType = chartType === "pie" || chartType === "doughnut";

  var datasets = (chart.datasets || []).map(function (ds, i) {
    var bgColor = ds.color || PALETTE[i % PALETTE.length];

    if (isPieType && (!ds.backgroundColor || !Array.isArray(ds.backgroundColor))) {
      bgColor = (ds.data || []).map(function (_, j) {
        return PALETTE[j % PALETTE.length];
      });
    }

    var mapped = {
      label: ds.label || "",
      data: ds.data || [],
      backgroundColor: bgColor,
      borderColor: isPieType ? "#fff" : (ds.color || PALETTE[i % PALETTE.length]),
      borderWidth: 1,
    };

    if (ds.stack) {
      mapped.stack = ds.stack;
    }

    return mapped;
  });

  var data = {
    labels: chart.labels,
    datasets: datasets,
  };

  var baseOptions = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: { size: 12, family: "'DM Sans'" },
          padding: 14,
        },
      },
      tooltip: {
        cornerRadius: 6,
        padding: 10,
        bodyFont: { size: 12 },
        titleFont: { size: 12, weight: 600 },
        callbacks: {
          label: function (context) {
            var label = context.dataset.label || "";
            var val = context.parsed.y != null ? context.parsed.y : context.parsed;
            if (typeof val === "number") {
              val = val.toLocaleString("en-US");
            }
            return label ? label + ": " + val : val;
          },
        },
      },
    },
  };

  var labelCount = chart.labels ? chart.labels.length : 0;

  var scaleOptions = {
    scales: {
      x: {
        grid: { color: "#f3f4f6" },
        ticks: {
          font: { size: 11, family: "'IBM Plex Sans'" },
          color: "#6b7280",
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: labelCount > 20 ? Math.ceil(labelCount / 3) :
                         labelCount > 12 ? Math.ceil(labelCount / 2) :
                         labelCount,
        },
      },
      y: {
        grid: { color: "#f3f4f6" },
        ticks: {
          font: { size: 11, family: "'IBM Plex Sans'" },
          color: "#6b7280",
          callback: function (value) {
            return typeof value === "number" ? value.toLocaleString("en-US") : value;
          },
        },
      },
    },
  };

  function handleCopy() {
    var lines = [[""].concat(chart.labels).join(",")];
    (chart.datasets || []).forEach(function (ds) {
      lines.push([ds.label || ""].concat(ds.data || []).join(","));
    });
    var csv = lines.join("\n");
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(function () { setCopied(false); }, 1500);
    }
  }

  function renderChart() {
    switch (chartType) {
      case "bar":
        return <Bar data={data} options={Object.assign({}, baseOptions, scaleOptions)} />;

      case "horizontal_bar":
        return (
          <Bar
            data={data}
            options={Object.assign({}, baseOptions, {
              indexAxis: "y",
              scales: scaleOptions.scales,
            })}
          />
        );

      case "grouped_bar":
        return <Bar data={data} options={Object.assign({}, baseOptions, scaleOptions)} />;

      case "stacked_bar":
        return (
          <Bar
            data={data}
            options={Object.assign({}, baseOptions, {
              scales: {
                x: Object.assign({}, scaleOptions.scales.x, { stacked: true }),
                y: Object.assign({}, scaleOptions.scales.y, { stacked: true }),
              },
            })}
          />
        );

      case "line":
        var lineDatasets = datasets.map(function (ds) {
          return Object.assign({}, ds, { tension: 0.3, pointRadius: 3, fill: false });
        });
        return (
          <Line
            data={Object.assign({}, data, { datasets: lineDatasets })}
            options={Object.assign({}, baseOptions, scaleOptions)}
          />
        );

      case "pie":
        return <Pie data={data} options={baseOptions} />;

      case "doughnut":
        return (
          <Doughnut
            data={data}
            options={Object.assign({}, baseOptions, {
              cutout: "60%",
            })}
          />
        );

      case "waterfall":
        var wfData = buildWaterfallData(chart);
        var origValues = (chart.datasets && chart.datasets[0] && chart.datasets[0].data) || [];
        var wfOptions = Object.assign({}, baseOptions, {
          plugins: Object.assign({}, baseOptions.plugins, {
            tooltip: {
              cornerRadius: 6,
              padding: 10,
              bodyFont: { size: 12 },
              titleFont: { size: 12, weight: 600 },
              callbacks: {
                label: function (context) {
                  var orig = origValues[context.dataIndex];
                  if (typeof orig !== "number") return "";
                  var label = context.dataset.label || "";
                  return (label ? label + ": " : "") + orig.toLocaleString("en-US");
                },
              },
            },
          }),
          scales: scaleOptions.scales,
        });
        return <Bar data={wfData} options={wfOptions} />;

      case "heatmap":
        var hmLabels = chart.labels || [];
        var hmDatasets = chart.datasets || [];
        var hmMax = 0;
        hmDatasets.forEach(function (ds) {
          (ds.data || []).forEach(function (v) { if (v > hmMax) hmMax = v; });
        });
        var hmColCount = hmLabels.length;
        return (
          <div style={{ overflowX: "auto" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px repeat(" + hmColCount + ", 1fr)",
              gap: "2px",
              minWidth: (130 + hmColCount * 72) + "px",
            }}>
              <div />
              {hmLabels.map(function (lbl, j) {
                return (
                  <div key={j} style={{
                    padding: "4px",
                    fontSize: "10.5px",
                    fontWeight: 600,
                    color: "#6B7280",
                    textAlign: "center",
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {lbl}
                  </div>
                );
              })}
              {hmDatasets.map(function (ds, ri) {
                var rowColor = ds.color || "#003087";
                return (
                  <React.Fragment key={ri}>
                    <div style={{
                      padding: "8px",
                      fontSize: "11.5px",
                      fontWeight: 500,
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {ds.label}
                    </div>
                    {(ds.data || []).map(function (val, ci) {
                      var intensity = hmMax > 0 ? val / hmMax : 0;
                      var bgColor = interpolateColor(rowColor, intensity);
                      var textColor = intensity > 0.55 ? "#fff" : "#374151";
                      return (
                        <div key={ci} style={{
                          backgroundColor: bgColor,
                          padding: "8px 4px",
                          textAlign: "center",
                          fontSize: "10.5px",
                          fontWeight: 500,
                          color: textColor,
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          borderRadius: "3px",
                          lineHeight: 1.2,
                        }}>
                          {formatHeatmapValue(val)}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );

      default:
        return <Bar data={data} options={Object.assign({}, baseOptions, scaleOptions)} />;
    }
  }

  var chartH = (chartType === "heatmap") ? null : (isExpanded ? 600 : getChartHeight(chart.labels, chartType));

  return (
    <div className={classes.wrapper}>
      <button
        className={copied ? classes.copyBtn + " " + classes.copyBtnCopied : classes.copyBtn}
        onClick={handleCopy}
        title="Copy as CSV"
      >
        {copied ? "Copied!" : <FileCopyOutlinedIcon style={{ fontSize: 14, color: "#6b7280" }} />}
      </button>
      {chartType !== "heatmap" && (
        <button
          className={classes.expandBtn}
          onClick={function () { setIsExpanded(!isExpanded); }}
          title={isExpanded ? "Collapse chart" : "Expand chart"}
        >
          {isExpanded ? "Collapse" : "Expand"}
        </button>
      )}
      {chart.title && <div className={classes.title}>{chart.title}</div>}
      <div className={classes.chartContainer} style={chartH != null ? { height: chartH + "px" } : {}}>
        {renderChart()}
      </div>
    </div>
  );
});

export default ChartRenderer;
